import { DeepQueryParams, QueryParams } from '@gik/core/api';

interface PostsUrlBuilderParams {
  'gik-preview'?: number;
  slug?: string;
  author_slug?: string;
  per_page?: number;
  orderby?: string;
  order?: string;
  meta_key?: string;
  meta_product_bundles?: number;
  meta_buying_guides?: number;
  filter?: DeepQueryParams;
}

export interface RelationFilter<T = string | string[]> {
  taxonomy: string;
  terms: T;
  field?: string;
}

export class PostsUrlBuilder {
  private readonly _params: PostsUrlBuilderParams;
  private readonly defaultTaxonomyItemFieldValue = 'slug';

  constructor() {
    this._params = {};

    // TODO: previewMode should come from a cookie
    this.withPreviewMode(true);
  }

  /**
   * get parameters as a QueryParams or DeepQueryParams object usable by the fetchers
   */
  get params(): QueryParams | DeepQueryParams {
    return JSON.parse(JSON.stringify(this._params));
  }

  withSlug(slug: string) {
    if (slug) {
      this._params.slug = slug;
    } else {
      delete this._params.slug;
    }

    return this;
  }

  withPreviewMode(enabled = true) {
    if (enabled) {
      this._params['gik-preview'] = 1;
    } else {
      delete this._params['gik-preview'];
    }
    return this;
  }

  withPerPage(perPage: number) {
    this._params.per_page = perPage;
    return this;
  }

  byAuthor(authorSlug: string) {
    this._params.filter = this._params.filter || {};
    this._params.filter.author_slug = authorSlug;
    return this;
  }

  withSingleTaxonomyItem(relationFilters: RelationFilter<string>) {
    this._params.filter = {
      tax_query: {
        relation: 'AND',
        0: {
          taxonomy: relationFilters.taxonomy,
          field: relationFilters.field || this.defaultTaxonomyItemFieldValue,
          operator: 'IN',
          include_children: 0,
          terms: relationFilters.terms,
        },
      },
    };

    return this;
  }

  withSingleTaxonomyMultipleItems(relationFilters: RelationFilter<string[]>) {
    this._params.filter = {
      tax_query: {
        relation: 'AND',
        0: {
          taxonomy: relationFilters.taxonomy,
          field: relationFilters.field || this.defaultTaxonomyItemFieldValue,
          operator: 'IN',
          include_children: 0,
          terms: relationFilters.terms,
        },
      },
    };

    return this;
  }

  withMultipleTaxonomiesMultipleItems(relationFilters: RelationFilter<string[]>[]) {
    if (relationFilters) {
      this._params.filter = {
        tax_query: {
          relation: 'AND',
          ...relationFilters?.reduce(
            (acc, relationFilter, i) => ({
              ...acc,
              [i]: {
                taxonomy: relationFilter.taxonomy,
                field: relationFilter.field || this.defaultTaxonomyItemFieldValue,
                operator: 'IN',
                include_children: 0,
                terms: relationFilter.terms,
              },
            }),
            {}
          ),
        },
      };
    }

    return this;
  }

  withMeta(metaKey: string, metaValue: number | string) {
    this._params.meta_key = metaKey;
    this._params.filter = {
      meta_value: metaValue,
      meta_compare: 'LIKE',
    };

    return this;
  }

  withProductBundles(enabled = true) {
    if (enabled) {
      this._params.meta_product_bundles = 1;
    } else {
      delete this._params.meta_product_bundles;
    }

    return this;
  }

  withBuyingGuides(enabled = true) {
    if (enabled) {
      this._params.meta_buying_guides = 1;
    } else {
      delete this._params.meta_buying_guides;
    }

    return this;
  }

  orderedByDate() {
    this._params.orderby = 'date';
    this._params.order = 'desc';

    return this;
  }

  orderedByPriority() {
    this._params.orderby = 'meta_value_num';
    this._params.order = 'desc';
    this._params.meta_key = 'priority';

    return this;
  }
}

export default PostsUrlBuilder;
